import { useState } from "react";
import { toast } from "sonner";
import { Headphones, PhoneCall, Video, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useBranding } from "@/lib/branding";

const AGENT_WAIT = "under 2 min";

export default function RemoteAssistDialog({ open, onOpenChange }) {
  const { brand } = useBranding();
  const AGENT = { name: "Priya (IT Tech)", team: `${brand.orgName} Digital Workplace`, avg_wait: AGENT_WAIT };
  const [stage, setStage] = useState("intake"); // intake | connecting | connected
  const [issue, setIssue] = useState("");

  const start = () => {
    if (!issue.trim()) { toast.error("Describe your issue briefly"); return; }
    setStage("connecting");
    setTimeout(() => setStage("connected"), 1600);
  };

  const close = (o) => {
    onOpenChange(o);
    if (!o) { setStage("intake"); setIssue(""); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="remote-assist-dialog" className="max-w-md bg-white">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Headphones size={12} className="text-[#0033A0]" /> Remote Assist
          </div>
          <DialogTitle className="font-display text-2xl text-slate-900">Connect with an IT Technician</DialogTitle>
          <DialogDescription>{AGENT.team} agents are online. Average wait: {AGENT.avg_wait}.</DialogDescription>
        </DialogHeader>

        {stage === "intake" && (
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">What do you need help with?</div>
              <Textarea data-testid="assist-issue" value={issue} onChange={(e) => setIssue(e.target.value)} placeholder="e.g. VPN not connecting, laptop won't boot…" className="mt-1 min-h-20" />
            </div>
            <Button onClick={start} data-testid="assist-start" className="bg-[#0033A0] hover:bg-[#002573] text-white w-full">
              <PhoneCall size={14} className="mr-2" /> Start session
            </Button>
          </div>
        )}

        {stage === "connecting" && (
          <div className="py-10 text-center" data-testid="assist-connecting">
            <Loader2 className="animate-spin mx-auto text-[#0033A0]" size={32} />
            <div className="mt-3 text-sm text-slate-600">Routing to the next available technician…</div>
          </div>
        )}

        {stage === "connected" && (
          <div className="space-y-4" data-testid="assist-connected">
            <div className="border border-slate-200 rounded-md p-3 bg-green-50 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0033A0] text-white grid place-items-center font-bold">P</div>
              <div>
                <div className="font-semibold text-slate-900">{AGENT.name}</div>
                <div className="text-xs text-slate-600">{AGENT.team}</div>
              </div>
              <CheckCircle2 className="text-green-600 ml-auto" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" data-testid="assist-chat" onClick={() => toast.success("Chat opened (mock)")}>
                <MessageSquare size={14} className="mr-1.5" /> Chat
              </Button>
              <Button variant="outline" data-testid="assist-call" onClick={() => toast.success("Audio call started (mock)")}>
                <PhoneCall size={14} className="mr-1.5" /> Call
              </Button>
              <Button variant="outline" data-testid="assist-screen" onClick={() => toast.success("Screen share requested (mock)")}>
                <Video size={14} className="mr-1.5" /> Screen
              </Button>
            </div>
            <div className="text-xs text-slate-500 text-center">Session is a demo. In production this launches BeyondTrust / Zoom Rooms.</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
